import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { STATUS_LABEL } from "@/lib/constants";
import StageAccordion from "@/components/StageAccordion";
import { stageForStatus, stageState } from "@/lib/stageProgress";
import {
  Wrench,
  CheckCircle2,
  Circle,
  Clock,
  Package,
  ShieldCheck,
  Video,
  Play,
  X,
  MessageCircle,
  Building2,
  Calendar,
  User,
  Gamepad2,
  Smartphone,
  Laptop,
  Tv,
  Cpu,
  AlertTriangle,
  XCircle,
  ShieldAlert,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Estilos claros y de alto contraste por estado (para las badges/pills)
const STATUS_PILL = {
  RECEIVED: "bg-blue-50 text-blue-800 border-blue-200",
  IN_DIAGNOSIS: "bg-amber-50 text-amber-800 border-amber-200",
  WAITING_APPROVAL: "bg-amber-50 text-amber-800 border-amber-200",
  WAITING_PARTS: "bg-amber-50 text-amber-800 border-amber-200",
  IN_REPAIR: "bg-amber-50 text-amber-800 border-amber-200",
  TESTING: "bg-sky-50 text-sky-800 border-sky-200",
  REPAIRED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  IN_TRANSFER: "bg-violet-50 text-violet-800 border-violet-200",
  READY: "bg-emerald-50 text-emerald-800 border-emerald-200",
  DELIVERED: "bg-slate-100 text-slate-700 border-slate-200",
  NOT_REPAIRABLE: "bg-red-50 text-red-800 border-red-200",
  CLIENT_REJECTED: "bg-red-50 text-red-800 border-red-200",
  IN_WARRANTY: "bg-indigo-50 text-indigo-800 border-indigo-200",
};

// Ícono representativo del equipo por nombre de categoría
function EquipmentIcon({ category, className = "" }) {
  const c = (category || "").toLowerCase();
  const props = { size: 28, className };
  if (c.includes("consola") || c.includes("control")) return <Gamepad2 {...props} />;
  if (c.includes("teléfono") || c.includes("celular") || c.includes("móvil")) return <Smartphone {...props} />;
  if (c.includes("laptop") || c.includes("notebook") || c.includes("computador")) return <Laptop {...props} />;
  if (c.includes("tv") || c.includes("televisor")) return <Tv {...props} />;
  return <Cpu {...props} />;
}

// Bloque destacado según estado especial
function SpecialStateBlock({ status, warranty, notRepairableReason, notRepairableAt }) {
  if (status === "READY") {
    return (
      <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5 flex items-start gap-3" data-testid="public-special-ready">
        <div className="w-11 h-11 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
          <CheckCircle2 className="text-white" size={22} />
        </div>
        <div>
          <div className="font-heading font-extrabold text-lg text-emerald-900">Tu equipo está listo para retirar</div>
          <div className="text-sm text-emerald-800 mt-0.5">Podés pasarlo a buscar por la sucursal.</div>
        </div>
      </div>
    );
  }
  if (status === "DELIVERED") {
    return (
      <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-4 flex items-start gap-3" data-testid="public-special-delivered">
        <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center shrink-0">
          <CheckCircle2 className="text-white" size={20} />
        </div>
        <div className="flex-1">
          <div className="font-heading font-bold text-slate-900">Equipo retirado por el cliente</div>
          {warranty?.deadline_at && warranty.active && (
            <div className="text-xs text-emerald-700 mt-0.5">
              🛡️ Cubierto por garantía · {warranty.days_remaining} día{warranty.days_remaining !== 1 ? "s" : ""} restantes
            </div>
          )}
          {warranty?.deadline_at && !warranty.active && (
            <div className="text-xs text-slate-500 mt-0.5">
              Garantía vencida el {new Date(warranty.deadline_at).toLocaleDateString("es-AR")}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (status === "IN_WARRANTY") {
    return (
      <div className="rounded-2xl border-2 border-indigo-300 bg-indigo-50 p-5 flex items-start gap-3" data-testid="public-special-warranty">
        <div className="w-11 h-11 rounded-full bg-indigo-600 flex items-center justify-center shrink-0">
          <ShieldAlert className="text-white" size={22} />
        </div>
        <div>
          <div className="font-heading font-extrabold text-lg text-indigo-900">Atención en garantía</div>
          <div className="text-sm text-indigo-800 mt-0.5">Tu equipo está siendo revisado bajo cobertura de garantía.</div>
        </div>
      </div>
    );
  }
  if (status === "NOT_REPAIRABLE") {
    return (
      <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-5" data-testid="public-special-not-repairable">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-red-600 flex items-center justify-center shrink-0">
            <XCircle className="text-white" size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-extrabold text-lg text-red-900">Equipo no reparable</div>
            <div className="text-sm text-red-800 mt-0.5">El técnico determinó que no se puede reparar. Contactá al taller.</div>
          </div>
        </div>
        {notRepairableReason && (
          <div className="mt-3 rounded-xl bg-white border border-red-200 p-3" data-testid="public-not-repairable-reason">
            <div className="text-[11px] uppercase tracking-widest text-red-700 font-mono font-bold mb-1">Motivo</div>
            <div className="text-sm text-slate-900 whitespace-pre-wrap">{notRepairableReason}</div>
            {notRepairableAt && (
              <div className="text-[11px] text-slate-500 mt-2">
                Registrado el {new Date(notRepairableAt).toLocaleString()}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  if (status === "CLIENT_REJECTED") {
    return (
      <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-5 flex items-start gap-3" data-testid="public-special-rejected">
        <div className="w-11 h-11 rounded-full bg-red-600 flex items-center justify-center shrink-0">
          <AlertTriangle className="text-white" size={22} />
        </div>
        <div>
          <div className="font-heading font-extrabold text-lg text-red-900">Cliente rechazó reparación</div>
          <div className="text-sm text-red-800 mt-0.5">Contactá al taller para coordinar el retiro del equipo.</div>
        </div>
      </div>
    );
  }
  return null;
}

function ExtendedRepairPublicBanner({ ext }) {
  if (!ext?.deadline_at) return null;
  const d = new Date(ext.deadline_at);
  const isValid = !isNaN(d.getTime());
  return (
    <section
      className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5 sm:p-6"
      data-testid="public-extended-repair-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center flex-shrink-0 text-2xl">
          🕐
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-mono text-amber-800 mb-1">
            Estado especial
          </div>
          <div className="font-heading font-extrabold text-slate-900 text-lg sm:text-xl leading-tight">
            Reparación extendida
          </div>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">
            Esta reparación requiere más tiempo del inicialmente estimado debido a la complejidad del proceso.
            Nuestro técnico continúa trabajando en tu equipo.
          </p>
          {isValid && (
            <div className="mt-3 rounded-xl bg-white border border-amber-200 p-3" data-testid="public-extended-repair-deadline-block">
              <div className="text-[10px] uppercase tracking-widest font-mono text-amber-800 mb-0.5">
                Fecha estimada de finalización
              </div>
              <div className="font-heading font-extrabold text-amber-900 text-lg sm:text-xl" data-testid="public-extended-repair-deadline">
                {d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function PublicOrder() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [openStages, setOpenStages] = useState(null); // {diagnosis, before, during, after, delivery}
  const [viewer, setViewer] = useState(null); // { type: 'photo'|'video', src, error }

  const openViewer = useCallback((item) => {
    if (!item) return;
    setViewer({ type: item.media_type === "video" ? "video" : "photo", src: item.data, error: false });
  }, []);
  const closeViewer = useCallback(() => setViewer(null), []);

  useEffect(() => {
    if (!viewer) return;
    const onKey = (e) => { if (e.key === "Escape") closeViewer(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [viewer, closeViewer]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function load() {
    setLoading(true);
    setErr("");
    return axios
      .get(`${API}/public/orders/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const msg = e?.response?.data?.detail || "No se pudo cargar esta orden.";
        setErr(typeof msg === "string" ? msg : "Orden no disponible.");
      })
      .finally(() => setLoading(false));
  }

  // Auto-abre la etapa correspondiente al estado actual la primera vez que se carga la orden
  useEffect(() => {
    if (data && openStages === null) {
      const current = stageForStatus(data.status);
      setOpenStages({
        diagnosis: current === "diagnosis",
        before: current === "before",
        during: current === "during",
        after: current === "after",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  function toggleStage(key) {
    setOpenStages((prev) => ({ ...(prev || {}), [key]: !(prev && prev[key]) }));
  }

  const equipmentName = data?.subcategory?.name || data?.model || data?.brand || "Equipo";

  const waMessage = data
    ? `Hola, quiero consultar sobre mi reparación ${data.order_number}.`
    : "";
  const waHref = `https://wa.me/?text=${encodeURIComponent(waMessage)}`;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900">
      {/* Header limpio, compacto */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
              <Wrench size={18} className="text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-base tracking-tight text-slate-900">
                Punto<span className="text-blue-600">Play</span>
                <span className="text-slate-400"> · </span>
                Punto<span className="text-blue-600">Cell</span>
              </div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">
                Seguimiento de reparación
              </div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-600 font-mono">
            <ShieldCheck size={12} /> Solo lectura
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 pb-24 space-y-4">
        {loading && (
          <div className="text-center py-16 text-slate-500 font-mono" data-testid="public-order-loading">
            Cargando orden…
          </div>
        )}

        {!loading && err && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center" data-testid="public-order-error">
            <Package size={40} className="mx-auto text-red-500 mb-3" />
            <h2 className="font-heading font-bold text-xl text-red-700 mb-1">Orden no encontrada</h2>
            <p className="text-slate-600 text-sm">
              El código escaneado no coincide con ninguna reparación registrada.
              Comunícate con el taller si crees que es un error.
            </p>
          </div>
        )}

        {!loading && !err && data && (
          <>
            {/* Número de orden + Estado */}
            <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="public-order-header">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Orden</div>
                  <div
                    data-testid="public-order-number"
                    className="font-mono text-slate-900 text-2xl sm:text-3xl font-extrabold tracking-wider break-all"
                  >
                    {data.order_number}
                  </div>
                </div>
                <div
                  data-testid="public-order-status"
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border ${STATUS_PILL[data.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}
                >
                  {STATUS_LABEL[data.status] || data.status}
                </div>
              </div>
            </section>

            {/* Bloque especial según estado */}
            <SpecialStateBlock
              status={data.status}
              warranty={data.warranty}
              notRepairableReason={data.not_repairable_reason}
              notRepairableAt={data.not_repairable_at}
            />

            {/* Reparación extendida — banner tranquilizador cuando aplica */}
            {data.extended_repair?.active && (
              <ExtendedRepairPublicBanner ext={data.extended_repair} />
            )}

            {/* Equipo — hero principal */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="public-order-equipment">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Equipo</div>
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0 text-blue-700">
                  <EquipmentIcon category={data.category?.name} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900 leading-tight break-words" data-testid="public-equipment-name">
                    {equipmentName}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    {data.brand && <span data-testid="public-equipment-brand">{data.brand}</span>}
                    {data.brand && data.model && <span className="text-slate-400"> · </span>}
                    {data.model && <span data-testid="public-equipment-model">{data.model}</span>}
                    {!data.brand && !data.model && data.category?.name && (
                      <span>{data.category.name}</span>
                    )}
                  </div>
                </div>
              </div>

              {data.problem_reported && (
                <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3.5" data-testid="public-order-problem-wrap">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">
                    Problema reportado
                  </div>
                  <div className="text-sm text-slate-900 whitespace-pre-wrap leading-relaxed" data-testid="public-order-problem">
                    {data.problem_reported}
                  </div>
                </div>
              )}
            </section>

            {/* Otros equipos — sólo cuando la orden tiene múltiples equipos */}
            {(data.devices?.length || 0) > 1 && (
              <section className="rounded-2xl border-2 border-cyan-300 bg-cyan-50/60 p-4 sm:p-5" data-testid="public-order-devices">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h3 className="font-heading font-bold text-lg text-slate-900">
                    Todos los equipos en tu orden ({data.devices_summary?.total || data.devices.length})
                  </h3>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-mono font-bold ${
                      (data.devices_summary?.pending || 0) === 0
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                        : "bg-amber-100 text-amber-800 border border-amber-300"
                    }`}
                    data-testid="public-devices-summary"
                  >
                    {data.devices_summary?.completed || 0} de {data.devices_summary?.total || data.devices.length} completados
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.devices.map((d, i) => (
                    <div key={d.id} className="rounded-xl bg-white border border-slate-200 p-3" data-testid={`public-device-${i}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-[10px] font-mono uppercase text-slate-500">
                          Equipo #{i + 1}{d.is_primary ? " · Principal" : ""}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_PILL[d.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                          {STATUS_LABEL[d.status] || d.status}
                        </span>
                      </div>
                      <div className="mt-1 font-heading font-bold text-slate-900 text-base leading-snug">
                        {d.subcategory?.name || d.model || d.brand || d.category?.name || "Equipo"}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-600">
                        {d.brand && <span>{d.brand}</span>}
                        {d.brand && d.model && <span className="text-slate-400"> · </span>}
                        {d.model && <span>{d.model}</span>}
                      </div>
                      {d.extended_repair?.active && d.extended_repair?.deadline_at && (
                        <div
                          className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5"
                          data-testid={`public-device-${i}-extended-repair`}
                        >
                          <div className="text-[9px] uppercase tracking-widest font-mono text-amber-800 leading-none">
                            🕐 Reparación extendida
                          </div>
                          <div className="mt-0.5 text-[11px] text-amber-900 font-bold">
                            Fecha estimada: {new Date(d.extended_repair.deadline_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Info secundaria: sucursal / fechas */}
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="public-order-meta">
              <MetaCard icon={Building2} label="Sucursal">
                {data.current_branch?.name || data.reception_branch?.name || "—"}
              </MetaCard>
              <MetaCard icon={Calendar} label="Fecha de ingreso">
                {new Date(data.created_at).toLocaleDateString("es-AR")}
              </MetaCard>
              {data.updated_at && (
                <MetaCard icon={Clock} label="Última actualización" className="sm:col-span-2">
                  {new Date(data.updated_at).toLocaleString("es-AR")}
                </MetaCard>
              )}
            </section>

            {/* Cliente */}
            {data.customer_name && (
              <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="public-order-customer-card">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <User size={16} className="text-slate-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Cliente</div>
                    <div className="text-base font-semibold text-slate-900 truncate" data-testid="public-order-customer">
                      {data.customer_name}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Timeline */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="public-order-timeline">
              <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2 text-slate-900">
                <Clock size={18} className="text-blue-600" /> Progreso de la reparación
              </h3>
              <Timeline
                progressStates={data.progress_states}
                events={data.timeline}
                currentStatus={data.status}
              />
            </section>

            {/* ETAPAS PLEGABLES DEL FLUJO DE REPARACIÓN */}
            {(() => {
              const stages = [
                { key: "diagnosis", label: "Diagnóstico", description: "Falla detectada por el técnico", catKey: "DIAGNOSIS" },
                { key: "before", label: "Antes", description: "Estado del equipo al recibirlo", catKey: "BEFORE" },
                { key: "during", label: "Durante reparación", description: "Proceso de trabajo", catKey: "DURING" },
                { key: "after", label: "Reparado / Finalizado", description: "Equipo terminado", catKey: "AFTER" },
              ];
              const photos = data.photos || [];
              const diag = data.diagnosis;
              const hasDiag = diag && diag.modality && diag.modality !== "NONE";
              return stages.map((st, idx) => {
                const catPhotos = photos.filter((p) => p.category === st.catKey);
                const isDiag = st.key === "diagnosis";
                const count = isDiag
                  ? (hasDiag ? (diag.text ? 1 : 0) + (diag.media?.length || 0) : 0) + catPhotos.length
                  : catPhotos.length;
                // Etapa vacía y no es la actual → mostrar el header pero sin body atractivo
                const isCurrent = stageState(st.key, data.status) === "current";
                if (!isCurrent && count === 0) {
                  // Igual la renderizamos plegada — coherencia visual + puede abrirse manualmente
                }
                return (
                  <StageAccordion
                    key={st.key}
                    stepNumber={idx + 1}
                    label={st.label}
                    description={st.description}
                    state={stageState(st.key, data.status)}
                    isOpen={!!(openStages && openStages[st.key])}
                    onToggle={() => toggleStage(st.key)}
                    count={count > 0 ? `${count} ${count === 1 ? "elemento" : "elementos"}` : "sin novedad"}
                    testid={`public-stage-${st.key}`}
                  >
                    {isDiag && hasDiag && (
                      <div className="mb-3" data-testid="public-order-diagnosis">
                        {diag.text && (
                          <div className="rounded-xl bg-blue-50 border border-blue-200 p-3.5 text-sm text-slate-900 whitespace-pre-wrap mb-3 leading-relaxed" data-testid="public-diagnosis-text">
                            {diag.text}
                          </div>
                        )}
                        {diag.media && diag.media.length > 0 && (
                          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="public-diagnosis-media">
                            {diag.media.map((p) => (
                              p.media_type === "video" ? (
                                <button
                                  type="button"
                                  key={p.id}
                                  onClick={() => openViewer(p)}
                                  className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-black hover:border-blue-400 transition-all"
                                  data-testid={`public-diag-video-${p.id}`}
                                  aria-label="Ver video"
                                >
                                  {p.thumbnail ? (
                                    <img src={p.thumbnail} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-slate-900" />
                                  )}
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                    <div className="rounded-full bg-white/95 p-2 shadow-lg">
                                      <Play size={16} className="text-slate-900 fill-slate-900 ml-0.5" />
                                    </div>
                                  </div>
                                  <div className="absolute top-1.5 left-1.5 flex items-center gap-1 bg-slate-900/80 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full font-mono pointer-events-none">
                                    <Video size={9} /> Video
                                  </div>
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  key={p.id}
                                  onClick={() => openViewer(p)}
                                  className="block aspect-square rounded-xl overflow-hidden border border-slate-200 hover:border-blue-400 transition-all"
                                  data-testid={`public-diag-photo-${p.id}`}
                                  aria-label="Ver foto"
                                >
                                  <img src={p.data} alt="" className="w-full h-full object-cover" />
                                </button>
                              )
                            ))}
                          </div>
                        )}
                        {diag.signed_by_name && (
                          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 flex items-center gap-2" data-testid="public-diagnosis-signature">
                            <span className="font-semibold">✓ Firmado por {diag.signed_by_name}</span>
                            {diag.signed_at && (
                              <span className="text-emerald-800">· {new Date(diag.signed_at).toLocaleString("es-AR")}</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Fotos/videos de la categoría de la etapa */}
                    {catPhotos.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2" data-testid={`public-stage-${st.key}-media`}>
                        {catPhotos.map((p) => {
                          const isVideo = p.media_type === "video";
                          if (isVideo) {
                            return (
                              <button
                                type="button"
                                key={p.id}
                                onClick={() => openViewer(p)}
                                className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-black hover:border-blue-400 transition-all"
                                data-testid={`public-video-${p.id}`}
                                aria-label="Ver video"
                              >
                                {p.thumbnail ? (
                                  <img src={p.thumbnail} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full bg-slate-900" />
                                )}
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                  <div className="rounded-full bg-white/95 p-2 shadow-lg">
                                    <Play size={18} className="text-slate-900 fill-slate-900 ml-0.5" />
                                  </div>
                                </div>
                                <div className="absolute top-1.5 left-1.5 flex items-center gap-1 bg-slate-900/80 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full font-mono pointer-events-none">
                                  <Video size={9} /> Video
                                </div>
                              </button>
                            );
                          }
                          return (
                            <button
                              type="button"
                              key={p.id}
                              onClick={() => openViewer(p)}
                              className="block aspect-square rounded-xl overflow-hidden border border-slate-200 hover:border-blue-400 transition-all"
                              data-testid={`public-photo-${p.id}`}
                              aria-label="Ver foto"
                            >
                              <img src={p.data} alt={st.label} className="w-full h-full object-cover" />
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      (!isDiag || !hasDiag) && (
                        <div className="text-center text-slate-500 text-sm py-4 border border-dashed border-slate-200 rounded-xl" data-testid={`public-stage-${st.key}-empty`}>
                          Aún no hay novedades en esta etapa.
                        </div>
                      )
                    )}
                  </StageAccordion>
                );
              });
            })()}

            {/* Presupuesto */}
            {data.budget && (data.budget.items?.length > 0 || data.budget.express_surcharge > 0) && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="public-order-budget">
                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                  <h3 className="font-heading font-bold text-lg text-slate-900">Presupuesto</h3>
                  {data.is_express && (
                    <span
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[11px] font-bold uppercase tracking-wider"
                      data-testid="public-express-badge"
                    >
                      ⚡ Reparación Express
                    </span>
                  )}
                </div>
                <ul className="divide-y divide-slate-100">
                  {data.budget.items.map((it) => (
                    <li key={it.id} className="py-2.5 flex items-start justify-between gap-3" data-testid={`public-budget-item-${it.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-900 font-medium">{it.name}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{it.quantity} × ${it.unit_price.toFixed(2)}</div>
                        {it.note && <div className="text-[11px] text-slate-500 italic">{it.note}</div>}
                      </div>
                      <div className="font-mono text-sm font-semibold text-slate-900 shrink-0">${it.subtotal.toFixed(2)}</div>
                    </li>
                  ))}
                  {data.budget.express_surcharge > 0 && (
                    <li className="py-2.5 flex items-start justify-between gap-3" data-testid="public-budget-express-surcharge">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-amber-800 font-medium flex items-center gap-1">⚡ Recargo Express</div>
                        <div className="text-[11px] text-slate-500 italic">Atención inmediata</div>
                      </div>
                      <div className="font-mono text-sm font-semibold text-amber-800 shrink-0">${data.budget.express_surcharge.toFixed(2)}</div>
                    </li>
                  )}
                </ul>
                <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between items-center">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Total</div>
                  <div className="text-2xl font-heading font-extrabold text-emerald-700 font-mono" data-testid="public-budget-total">
                    ${data.budget.total.toFixed(2)}
                  </div>
                </div>

                {/* Decisión del cliente */}
                <BudgetDecision
                  budget={data.budget}
                  qrToken={token}
                  isExpress={data.is_express}
                  onDecided={async () => {
                    // Refrescar la orden
                    try {
                      const r = await axios.get(`${API}/public/orders/${token}`);
                      setData(r.data);
                    } catch {}
                  }}
                />
              </section>
            )}

            {/* CTA WhatsApp */}
            <section className="pt-2">
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="public-order-whatsapp"
                className="flex items-center justify-center gap-2 w-full py-3.5 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-base font-bold shadow-sm transition-colors"
              >
                <MessageCircle size={20} /> Contactar por WhatsApp
              </a>
              <div className="mt-2 text-center text-[11px] text-slate-500 font-mono">
                El mensaje incluirá tu número de orden.
              </div>
            </section>

            {/* Políticas y garantía */}
            <PoliciesSection data={data} qrToken={token} onAccepted={load} />

            <div className="text-center text-[11px] text-slate-500 font-mono pt-2">
              Esta es una vista pública de solo lectura.
            </div>
          </>
        )}
      </main>

      {viewer && (
        <MediaViewer viewer={viewer} onClose={closeViewer} onError={() => setViewer((v) => (v ? { ...v, error: true } : v))} />
      )}
    </div>
  );
}

function MediaViewer({ viewer, onClose, onError }) {
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="public-media-viewer"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-3 right-3 sm:top-5 sm:right-5 z-10 w-11 h-11 rounded-full bg-white/95 hover:bg-white text-slate-900 flex items-center justify-center shadow-lg active:scale-95 transition-transform"
        aria-label="Cerrar"
        data-testid="public-media-viewer-close"
      >
        <X size={22} strokeWidth={2.5} />
      </button>

      <div
        className="relative w-full h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {viewer.error ? (
          <div className="max-w-sm text-center px-6 py-8 rounded-2xl bg-slate-900/80 border border-slate-700 text-white" data-testid="public-media-viewer-error">
            <AlertTriangle size={36} className="mx-auto mb-3 text-amber-400" />
            <div className="font-semibold text-base mb-1">No se pudo cargar el {viewer.type === "video" ? "video" : "archivo"}</div>
            <div className="text-sm text-slate-300">
              {viewer.type === "video"
                ? "El formato puede no ser compatible con tu dispositivo o hubo un problema al descargarlo."
                : "Hubo un problema al cargar la imagen. Intentá nuevamente en unos minutos."}
            </div>
          </div>
        ) : viewer.type === "video" ? (
          <video
            src={viewer.src}
            controls
            autoPlay
            playsInline
            preload="metadata"
            controlsList="nodownload"
            onContextMenu={(e) => e.preventDefault()}
            onError={onError}
            className="max-w-full max-h-full w-auto h-auto rounded-lg bg-black"
            data-testid="public-media-viewer-video"
          />
        ) : (
          <img
            src={viewer.src}
            alt=""
            onError={onError}
            className="max-w-full max-h-full w-auto h-auto object-contain select-none"
            draggable={false}
            data-testid="public-media-viewer-image"
          />
        )}
      </div>
    </div>
  );
}

function MetaCard({ icon: Icon, label, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3 ${className}`}>
      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
        <Icon size={16} className="text-slate-600" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
        <div className="text-sm font-semibold text-slate-900 truncate">{children}</div>
      </div>
    </div>
  );
}

function BudgetDecision({ budget, qrToken, onDecided, isExpress }) {
  const [confirming, setConfirming] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (budget.decision_by && (budget.budget_status === "APPROVED" || budget.budget_status === "REJECTED")) {
    const approved = budget.budget_status === "APPROVED";
    // Modo Express con AT_INTAKE: mostrar "Reparación autorizada"
    const isAtIntake = approved && budget.approval_channel === "AT_INTAKE";
    const title = !approved
      ? "Presupuesto rechazado"
      : (isExpress || isAtIntake ? "Reparación autorizada" : "Reparación aprobada");
    return (
      <div
        className={`mt-4 rounded-xl border-2 p-4 flex items-start gap-3 ${
          approved ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-red-300 bg-red-50 text-red-900"
        }`}
        data-testid="public-budget-decision-registered"
      >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${approved ? "bg-emerald-600" : "bg-red-600"}`}>
          {approved ? <CheckCircle2 className="text-white" size={20} /> : <XCircle className="text-white" size={20} />}
        </div>
        <div>
          <div className="font-heading font-extrabold text-base">
            {title}
          </div>
          <div className="text-xs mt-0.5 opacity-90">
            Registrado el {budget.decision_at ? new Date(budget.decision_at).toLocaleString("es-AR") : ""}
          </div>
        </div>
      </div>
    );
  }

  if (!budget.can_decide) {
    return (
      <div className="mt-3 text-[11px] text-slate-500 font-mono">
        Estado: {({
          PENDING_QUOTE: "Pendiente de presupuesto",
          QUOTE_SENT: "Presupuesto enviado",
          APPROVED: "Aprobado",
          REJECTED: "Rechazado",
          AUTHORIZED: "Reparación autorizada",
          COMPLETED: "Reparación completada",
        })[budget.budget_status] || budget.budget_status}
      </div>
    );
  }

  async function submit() {
    if (!confirming) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/public/orders/${qrToken}/budget-decision`, { decision: confirming });
      setConfirming(null);
      await onDecided?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo registrar tu decisión.";
      setError(typeof msg === "string" ? msg : "Error al registrar la decisión.");
    } finally {
      setSubmitting(false);
    }
  }

  if (confirming) {
    const isApprove = confirming === "APPROVED";
    return (
      <div
        className={`mt-4 rounded-xl border-2 p-4 ${isApprove ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}
        data-testid="public-budget-confirm"
      >
        <div className={`font-heading font-bold text-base mb-2 ${isApprove ? "text-emerald-900" : "text-red-900"}`}>
          {isApprove ? "¿Confirmás autorizar la reparación?" : "¿Seguro que deseas rechazar este presupuesto?"}
        </div>
        <div className={`text-sm mb-3 ${isApprove ? "text-emerald-800" : "text-red-800"}`}>
          {isApprove
            ? "El taller comenzará (o continuará) con la reparación por un total de $" + budget.total.toFixed(2) + "."
            : "El taller detendrá la reparación y podrás retirar tu equipo sin realizar los trabajos."}
        </div>
        {error && (
          <div className="mb-3 text-xs text-red-700 bg-white/70 rounded-lg px-2 py-1.5" data-testid="public-budget-error">
            {error}
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="public-budget-confirm-btn"
            className={`flex-1 py-3 rounded-xl font-bold text-white text-sm shadow-sm transition-colors disabled:opacity-50 ${
              isApprove ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {submitting ? "Enviando…" : isApprove ? "Sí, autorizar" : "Sí, rechazar"}
          </button>
          <button
            onClick={() => { setConfirming(null); setError(""); }}
            disabled={submitting}
            data-testid="public-budget-cancel-btn"
            className="py-3 px-4 rounded-xl bg-white text-slate-700 font-bold text-sm border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border-2 border-blue-200 bg-blue-50 p-4" data-testid="public-budget-decision">
      <div className="font-heading font-bold text-base text-blue-900 mb-1">¿Deseás autorizar esta reparación?</div>
      <div className="text-xs text-blue-800 mb-3">
        Revisá el detalle y elegí. La decisión quedará registrada en tu orden.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          onClick={() => setConfirming("APPROVED")}
          data-testid="public-budget-approve-btn"
          className="py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base flex items-center justify-center gap-2 shadow-sm transition-colors"
        >
          <CheckCircle2 size={18} /> Aceptar reparación
        </button>
        <button
          onClick={() => setConfirming("REJECTED")}
          data-testid="public-budget-reject-btn"
          className="py-3.5 rounded-xl bg-white text-red-700 border-2 border-red-300 hover:bg-red-50 font-bold text-base flex items-center justify-center gap-2 transition-colors"
        >
          <XCircle size={18} /> Rechazar reparación
        </button>
      </div>
    </div>
  );
}

function Timeline({ progressStates, events, currentStatus }) {
  const reachedMap = {};
  for (const e of events || []) {
    reachedMap[e.status] = e.at;
  }
  const currentIdx = progressStates.indexOf(currentStatus);

  return (
    <ol className="relative">
      {progressStates.map((st, idx) => {
        const at = reachedMap[st];
        const isCompleted = at != null || (currentIdx >= 0 && idx < currentIdx);
        const isCurrent = st === currentStatus;

        return (
          <li key={st} className="flex gap-3 pb-4 last:pb-0 relative" data-testid={`public-timeline-step-${st}`}>
            {/* Vertical connector */}
            {idx < progressStates.length - 1 && (
              <div
                className={`absolute left-[13px] top-7 bottom-0 w-0.5 ${
                  isCompleted ? "bg-blue-500" : "bg-slate-200"
                }`}
              />
            )}
            <div className="relative flex-shrink-0 mt-0.5 z-10">
              {isCurrent ? (
                <div className="w-7 h-7 rounded-full bg-blue-600 ring-4 ring-blue-200 flex items-center justify-center" data-testid={`public-timeline-current-${st}`}>
                  <div className="w-2.5 h-2.5 rounded-full bg-white" />
                </div>
              ) : isCompleted ? (
                <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center">
                  <CheckCircle2 size={16} className="text-white" />
                </div>
              ) : (
                <div className="w-7 h-7 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center">
                  <Circle size={10} className="text-slate-300" />
                </div>
              )}
            </div>
            <div className="flex-1 pt-0.5 min-w-0">
              <div
                className={`text-sm ${
                  isCurrent ? "font-extrabold text-blue-700" :
                  isCompleted ? "font-semibold text-slate-900" :
                  "text-slate-500"
                }`}
              >
                {STATUS_LABEL[st] || st}
              </div>
              {at && (
                <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                  {new Date(at).toLocaleString("es-AR")}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}


// ==========================================
// POLÍTICAS OFICIALES DE PUNTOPLAY / PUNTOCELL
// ==========================================
// Estas son las 16 cláusulas oficiales — no se resumen, no se reescriben.
const OFFICIAL_POLICIES = [
  "La garantía de 30 días continuos ofrecida para este producto cubre solamente el servicio realizado en su equipo. No cubre daños por uso indebido, modificación física del producto o actualizaciones del software, daño físico, golpe o raya, ni en equipos que denoten indicios de haber sido abiertos o expuestos a altos voltajes. De ser algunos de estos casos quedará sin efecto la garantía del servicio.",
  "Todo equipo que ingrese estará sujeto a una revisión según la falla que presente el mismo, se notificará el diagnóstico y presupuesto para su aprobación, el cual debe ser aprobado y firmado como soporte por parte del cliente.",
  "Solo serán reparados los equipos aprobados.",
  "No nos hacemos responsables por transacciones o negociaciones realizadas fuera de las instalaciones y normativas (recibos) de la empresa.",
  "Todo presupuesto será válido durante 2 días continuos a partir de la fecha de notificación al cliente. Luego de este período la cotización podrá estar sujeta a modificaciones por parte de la empresa.",
  "La empresa no se hace responsable por periféricos y/o componentes de los equipos dejados en la consola o entregados al personal, ya que los mismos no son aceptados.",
  "Las condiciones de garantía están sujetas a cambios sin previo aviso o notificación, según la garantía de nuestros proveedores.",
  "Las devoluciones serán aplicadas a aquellos equipos previamente verificados por la empresa donde se determine que la falla no está dentro de los servicios ofrecidos por la misma.",
  "Las garantías se recibirán de lunes a viernes en el horario comprendido de 10:00 am a 6:00 pm.",
  "Equipos apagados, mojados, con módulo WIFI-BLUETOOTH defectuoso o con errores NO TIENEN GARANTÍA.",
  "PASADOS los 30 días no nos hacemos responsables por su CONSOLA, CONTROL Y/O EQUIPO DEJADO EN EL LOCAL.",
  "La empresa realizará la notificación por distintos medios (llamada, correo electrónico o mensaje de texto), cuando su equipo se encuentre bajo el estatus de listo reparado o devolución. Pasados treinta días continuos la empresa enviará a depósito y el cliente deberá cancelar los días que permanezcan en el mismo.",
  "En los casos de devoluciones o reintegro se cobrará la revisión del equipo.",
  "La empresa no se hace responsable por el desconocimiento que el cliente posea al servicio que se le está realizando.",
  "La empresa no se hace responsable por juegos físicos, juegos digitales y data en el disco duro de su consola.",
  "Sin orden de reparación no se entregan los equipos sin EXCEPCIONES.",
];

function PoliciesSection({ data, qrToken, onAccepted }) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const acceptance = data?.policies_acceptance;
  const isAccepted = !!(acceptance && acceptance.accepted_at);

  async function submit() {
    setError("");
    if (!checked) {
      setError("Debes marcar la casilla para continuar.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/orders/${qrToken}/accept-policies`, { accepted: true });
      await onAccepted?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo registrar la aceptación.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="rounded-2xl border-2 border-slate-200 bg-white p-5 sm:p-6" data-testid="public-policies-section">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">📋</span>
        <h2 className="font-heading font-extrabold text-lg sm:text-xl text-slate-900">
          Políticas y garantía
        </h2>
      </div>

      {/* Garantía destacada */}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 mb-4 text-sm text-emerald-900">
        <div className="font-heading font-extrabold">🛡️ Garantía de reparación: 30 días</div>
        <div className="text-emerald-800 text-xs mt-0.5">
          Válida desde la fecha de entrega del equipo. Cubre únicamente el servicio realizado.
        </div>
      </div>

      {/* Lista oficial de 16 políticas */}
      <ol className="list-decimal list-outside pl-5 space-y-2.5 text-[13px] text-slate-800 leading-relaxed" data-testid="public-policies-list">
        {OFFICIAL_POLICIES.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ol>

      <div className="mt-6 pt-5 border-t-2 border-slate-100">
        {isAccepted ? (
          <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4" data-testid="public-policies-accepted">
            <div className="flex items-start gap-2">
              <span className="text-2xl leading-none">✓</span>
              <div>
                <div className="font-heading font-bold text-emerald-900">Políticas aceptadas</div>
                <div className="text-xs text-emerald-800 mt-1">
                  Fecha y hora: <b>{new Date(acceptance.accepted_at).toLocaleString()}</b>
                </div>
                {acceptance.version && (
                  <div className="text-[11px] text-emerald-700 mt-0.5">Versión: {acceptance.version}</div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="public-policies-checkbox-wrapper">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-1 w-5 h-5 accent-blue-600 shrink-0"
                data-testid="public-policies-checkbox"
              />
              <span className="text-sm text-slate-800">
                He leído y acepto las políticas y condiciones de reparación y garantía.
              </span>
            </label>
            {error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="public-policies-error">
                {error}
              </div>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !checked}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white py-3 font-heading font-bold text-base disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              data-testid="public-policies-accept-btn"
            >
              {submitting ? "Registrando…" : "Aceptar políticas"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
